import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, GripHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PlaygroundOutput } from '@/components/playground/PlaygroundOutput';
import { PlaygroundObserver } from '@/components/playground/PlaygroundObserver';
import { cn } from '@/lib/utils';

interface IDEExecutionPanelProps {
  height: number;
  onHeightChange: (height: number) => void;
  activeTab: 'output' | 'logs' | 'trace';
  onTabChange: (tab: 'output' | 'logs' | 'trace') => void;

  // Execution data
  output: string | null;
  error: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled' | 'hitl_pending';
  logs: string;
  traceEvents: any[];
  trajectoryId: string | null;
}

const MIN_HEIGHT = 100;
const MAX_HEIGHT = 500;
const DEFAULT_HEIGHT = 200;

export function IDEExecutionPanel({
  height,
  onHeightChange,
  activeTab,
  onTabChange,
  output,
  error,
  status,
  logs,
  traceEvents,
  trajectoryId,
}: IDEExecutionPanelProps) {
  const isCollapsed = height === 0;
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startYRef.current = e.clientY;
    startHeightRef.current = height || DEFAULT_HEIGHT;
  }, [height]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = startYRef.current - e.clientY;
      const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeightRef.current + delta));
      onHeightChange(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onHeightChange]);

  const toggleCollapse = useCallback(() => {
    if (isCollapsed) {
      onHeightChange(DEFAULT_HEIGHT);
    } else {
      onHeightChange(0);
    }
  }, [isCollapsed, onHeightChange]);

  if (isCollapsed) {
    return (
      <div className="border-t bg-muted/30">
        <div className="flex items-center justify-between px-4 py-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Execution Panel</span>
            {status !== 'idle' && (
              <span
                className={cn(
                  'text-xs px-2 py-0.5 rounded',
                  status === 'running' && 'bg-blue-100 text-blue-700',
                  status === 'completed' && 'bg-green-100 text-green-700',
                  status === 'failed' && 'bg-red-100 text-red-700',
                  status === 'cancelled' && 'bg-orange-100 text-orange-700',
                  status === 'hitl_pending' && 'bg-yellow-100 text-yellow-700'
                )}
              >
                {status === 'hitl_pending' ? 'awaiting input' : status}
              </span>
            )}
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={toggleCollapse}>
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className="border-t bg-background flex flex-col"
      style={{ height: `${height}px` }}
    >
      {/* Resize Handle */}
      <div
        className={cn(
          'h-1 cursor-ns-resize flex items-center justify-center hover:bg-muted/50 transition-colors',
          isDragging && 'bg-primary/20'
        )}
        onMouseDown={handleMouseDown}
      >
        <GripHorizontal className="h-3 w-3 text-muted-foreground" />
      </div>

      {/* Tab Bar */}
      <div className="flex items-center justify-between px-4 py-1 border-b shrink-0">
        <Tabs value={activeTab} onValueChange={(v) => onTabChange(v as 'output' | 'logs' | 'trace')}>
          <TabsList className="h-7">
            <TabsTrigger value="output" className="text-xs px-3">
              Output
            </TabsTrigger>
            <TabsTrigger value="logs" className="text-xs px-3">
              Logs
            </TabsTrigger>
            <TabsTrigger value="trace" className="text-xs px-3">
              Trace
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={toggleCollapse}>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'output' && (
          <div className="h-full p-4 overflow-auto">
            <PlaygroundOutput output={output} error={error} status={status} />
          </div>
        )}
        {(activeTab === 'logs' || activeTab === 'trace') && (
          <PlaygroundObserver
            logs={logs}
            traceEvents={traceEvents}
            trajectoryId={trajectoryId}
            activeTab={activeTab}
          />
        )}
      </div>
    </div>
  );
}

export default IDEExecutionPanel;
